import { mm, type Um } from './units.js';

export type BottomConstruction = 'between-sides' | 'under-sides';
export type TopConstruction = 'between-sides' | 'over-sides' | 'none';
export type BackMode = 'groove' | 'overlay' | 'none';
export type FrontMounting = 'overlay' | 'inset';
export type FrontRowKind = 'door-left' | 'door-right' | 'double-door' | 'drawer' | 'open';

export interface FrontRow {
  kind: FrontRowKind;
  /** Fixed front height (µm). `null` = share the remaining height equally. */
  height: Um | null;
}

export interface DrawerSystem {
  /** Only a generic manual model exists until real slide catalog data is imported. */
  kind: 'generic-manual';
  label: string;
  /** Clearance between carcass side and drawer box side, per side. Slide-specific! */
  sideClearance: Um;
  /** Box height = front height − this reduction. */
  boxHeightReduction: Um;
  /** Nominal slide/box length. Must fit the inner depth. */
  nominalLength: Um;
  boxSideThickness: Um;
  boxBottomThickness: Um;
}

export interface CabinetParams {
  /** Outer carcass width / height / depth (height excludes plinth, legs and worktop). */
  width: Um;
  height: Um;
  depth: Um;
  plinthHeight: Um;
  plinthSetback: Um;
  legsHeight: Um;

  sideThickness: Um;
  bottomThickness: Um;
  topThickness: Um;
  backThickness: Um;
  shelfThickness: Um;
  dividerThickness: Um;
  frontThickness: Um;

  bottomConstruction: BottomConstruction;
  topConstruction: TopConstruction;

  back: {
    mode: BackMode;
    /** Groove: distance from carcass back to the groove. */
    grooveInset: Um;
    grooveDepth: Um;
    /** Fitting clearance of the back panel in the groove, per side. */
    clearance: Um;
  };

  dividers: number;
  shelves: {
    /** Shelves per compartment. */
    count: number;
    frontSetback: Um;
    /** Clearance per side for loose shelves. */
    sideClearance: Um;
  };

  fronts: {
    mounting: FrontMounting;
    gapOuter: Um;
    gapBetween: Um;
    /** Rows from top to bottom. Empty = open cabinet. */
    rows: FrontRow[];
  };

  drawerSystem: DrawerSystem;
}

export const GENERIC_DRAWER_SYSTEM: DrawerSystem = {
  kind: 'generic-manual',
  label: 'Generička ručna vodilica (nije stvaran proizvod)',
  sideClearance: mm(12.5),
  boxHeightReduction: mm(40),
  nominalLength: mm(450),
  boxSideThickness: mm(16),
  boxBottomThickness: mm(8),
};

export function defaultCabinetParams(overrides: Partial<CabinetParams> = {}): CabinetParams {
  return {
    width: mm(600),
    height: mm(720),
    depth: mm(560),
    plinthHeight: 0,
    plinthSetback: mm(50),
    legsHeight: 0,
    sideThickness: mm(18),
    bottomThickness: mm(18),
    topThickness: mm(18),
    backThickness: mm(3),
    shelfThickness: mm(18),
    dividerThickness: mm(18),
    frontThickness: mm(18),
    bottomConstruction: 'between-sides',
    topConstruction: 'between-sides',
    back: { mode: 'groove', grooveInset: mm(10), grooveDepth: mm(8), clearance: mm(1) },
    dividers: 0,
    shelves: { count: 1, frontSetback: mm(2), sideClearance: mm(0.5) },
    fronts: { mounting: 'overlay', gapOuter: mm(1.5), gapBetween: mm(3), rows: [] },
    drawerSystem: GENERIC_DRAWER_SYSTEM,
    ...overrides,
  };
}
